// Tipos + loader del snapshot de precios públicos (asset estático bakeado en build).
// Las páginas públicas de precios leen SOLO este asset: cero llamadas runtime a Supabase.
// Ver docs/superpowers/specs/2026-07-29-landing-precios-publicos-design.md §4.

export interface PricePuntas {
  highest: number;
  lowest: number;
  average: number;
}

export interface CategoryPrices {
  perkg?: Record<string, PricePuntas>;
  perUnit?: Record<string, PricePuntas>;
}

export interface HistoryPoint {
  year: number;
  month: number;
  label: string; // "Ene" o "Ene 24"
  terneros: number | null;
  terneras: number | null;
  novillos: number | null;
  vaquillonas: number | null;
  vacas: number | null;
  toros: number | null;
  usd: number | null; // ARS por USD (blue) de ese mes (para graficar en USD)
  uva: number | null; // UVA de ese mes (para pesos constantes)
}

export interface PricesSnapshot {
  generatedAt: string;
  available: boolean;
  source: string; // "Promedio mensual de remates (provincia de Buenos Aires)"
  latestMonth: { year: number; month: number; label: string } | null;
  usd: {
    available: boolean;
    source: string;
    rate: number | null; // ARS por USD blue (más reciente)
    rateType: string; // "Blue"
    rateDate: string | null; // "YYYY-MM-DD"
  };
  uva: {
    available: boolean;
    source: string;
    today: number | null; // UVA más reciente (para pesos constantes)
    date: string | null;
  };
  /** Desglose completo del último mes, para /precios y la calculadora. */
  latest: Record<string, CategoryPrices>;
  /** Serie mensual completa (oldest→newest), para /historico-precios. */
  history: HistoryPoint[];
}

const FALLBACK: PricesSnapshot = {
  generatedAt: '',
  available: false,
  source: 'Promedio mensual de remates (provincia de Buenos Aires)',
  latestMonth: null,
  usd: { available: false, source: '', rate: null, rateType: '', rateDate: null },
  uva: { available: false, source: '', today: null, date: null },
  latest: {},
  history: [],
};

let cache: PricesSnapshot | null = null;
let inflight: Promise<PricesSnapshot> | null = null;

export function getEmptySnapshot(): PricesSnapshot {
  return JSON.parse(JSON.stringify(FALLBACK));
}

/**
 * Carga el snapshot estático `/prices-snapshot.json` (bakeado en build).
 * Una sola vez por sesión, con caché a nivel módulo. Ante cualquier error
 * devuelve un snapshot vacío (available:false) — las páginas muestran fallback.
 */
export async function loadPricesSnapshot(): Promise<PricesSnapshot> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const base = process.env.PUBLIC_URL || '';
      const res = await fetch(`${base}/prices-snapshot.json`, { cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as PricesSnapshot;
      cache = data && typeof data === 'object' ? data : getEmptySnapshot();
      return cache;
    } catch {
      cache = getEmptySnapshot();
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Cotización ARS/USD para una fecha dada, usando la serie mensual del snapshot
 * (la misma del BCRA que se hornea en build). Busca el mes de `date` en
 * `history[].usd`; si no existe (mes en curso/futuro o sin data) cae a `usd.rate`
 * (la más reciente). Devuelve null si no hay USD disponible.
 *
 * Uso típico: convertir un movimiento histórico a USD por el dólar DE SU mes.
 */
export function getUsdRateForDate(
  snap: PricesSnapshot | null,
  date: string | Date | null | undefined
): number | null {
  if (!snap) return null;
  const latest = snap.usd.available ? snap.usd.rate : null;
  if (date == null) return latest;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return latest;
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const point = snap.history.find((h) => h.year === y && h.month === m);
  if (point && point.usd) return point.usd;
  // Mes sin cotización propia (ej. mes en curso): usar la más reciente.
  return latest;
}

/**
 * UVA para una fecha dada (serie mensual del snapshot). Sin fecha → UVA más
 * reciente. Null si no hay UVA. Se usa para "pesos constantes":
 * monto_constante = ars * (uvaHoy / uvaDeLaFecha).
 */
export function getUvaForDate(
  snap: PricesSnapshot | null,
  date: string | Date | null | undefined
): number | null {
  if (!snap) return null;
  const today = snap.uva.available ? snap.uva.today : null;
  if (date == null) return today;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return today;
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const point = snap.history.find((h) => h.year === y && h.month === m);
  if (point && point.uva) return point.uva;
  return today;
}

/**
 * Precio promedio/kg de una categoría del último mes (media simple del campo
 * `average` entre los rangos perkg con average>0). 0 si la categoría no existe.
 */
export function getCategoryAvgKg(latest: Record<string, CategoryPrices>, animal: string): number {
  const cat = latest[animal];
  if (!cat || !cat.perkg) return 0;
  const vals = Object.values(cat.perkg)
    .map((p) => p.average)
    .filter((a) => Number.isFinite(a) && a > 0);
  if (vals.length === 0) return 0;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}
